import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InputShortText from "../../../../components/InputShortText/InputShortText";
import Notice from "../../../../components/Notice/Notice";
import SecondaryButton from "../../../../components/SecondaryButton/SecondaryButton";
import SmallCheckBox from "../../../../components/SmallCheckBox/SmallCheckBox";
import Subtitle from "../../../../components/Subtitle/Subtitle";
import TextArea from "../../../../components/TextArea/TextArea";
import { EmailHtmlDualEditor } from "../../../../components/EmailHtmlDualEditor/EmailHtmlDualEditor";
import { NotificationDeliveryToggle } from "../../../../components/NotificationDeliveryToggle/NotificationDeliveryToggle";
import TinyText from "../../../../components/TinyText/TinyText";
import RemindersAPI from "../../../../api/RemindersAPI";
import { toastService } from "../../../../services/ToastService";
import { normalizeEmailBodyFormat, type EmailBodyFormat } from "../../../../types/emailBodyFormat";
import { isValidText } from "../../../../shared/validation";
import { UpdateCallback } from "../../sections/NotificationsSegment";
import { stableSignatureWeddingRate } from "../reminderConfigStableSignature";

interface DefaultConfig {
  enabled?: boolean;
  emailTitle?: string;
  text?: string;
  emailBodyFormat?: EmailBodyFormat;
}

interface WeddingRateReminderProps {
  update: UpdateCallback;
  defaultConfig?: DefaultConfig;
  hideSectionTitle?: boolean;
}

export function WeddingRateReminder({ update, defaultConfig, hideSectionTitle }: WeddingRateReminderProps) {
  const [enabled, setEnabled] = useState(defaultConfig?.enabled || false);
  const [emailTitle, setEmailTitle] = useState(defaultConfig?.emailTitle || "");
  const [text, setText] = useState(defaultConfig?.text || "");
  const [emailBodyFormat, setEmailBodyFormat] = useState<EmailBodyFormat | undefined>(
    defaultConfig?.emailBodyFormat
  );
  const [testLoading, setTestLoading] = useState(false);
  const { t } = useTranslation();
  const lastEmittedSignatureRef = useRef<string | null>(null);

  const fmt = normalizeEmailBodyFormat(emailBodyFormat);

  useEffect(() => {
    if (!defaultConfig) return;
    const incoming = stableSignatureWeddingRate(defaultConfig);
    if (lastEmittedSignatureRef.current !== null && incoming === lastEmittedSignatureRef.current) {
      return;
    }
    setEnabled(defaultConfig.enabled || false);
    setEmailTitle(defaultConfig.emailTitle || "");
    setText(defaultConfig.text || "");
    setEmailBodyFormat(defaultConfig.emailBodyFormat);
  }, [defaultConfig]);

  useEffect(() => {
    const config = {
      enabled,
      type: "email" as const,
      emailTitle,
      text,
      emailBodyFormat: fmt,
    };
    lastEmittedSignatureRef.current = stableSignatureWeddingRate(config);
    update("weddingRateReminder", config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, emailTitle, text, fmt]);

  const injectDefaultTemplate = () => {
    setEmailTitle(String(t("notifications.wedding_rate_reminder.default_title")));
    setText(
      String(
        fmt === "html"
          ? t("notifications.wedding_rate_reminder.default_html")
          : t("notifications.wedding_rate_reminder.default_message")
      )
    );
  };

  const sendTest = async () => {
    if (!emailTitle.trim() || !text.trim()) {
      toastService.showToast(t("notifications.test_email_validation"), "error");
      return;
    }
    setTestLoading(true);
    try {
      await RemindersAPI.sendTestEmail({
        subject: emailTitle.trim(),
        htmlBody: text,
        emailBodyFormat: fmt,
      });
      toastService.showToast(t("notifications.test_email_ok"));
    } catch {
      toastService.showToast(t("notifications.test_email_fail"), "error");
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div>
      {!hideSectionTitle && <Subtitle text={t("notifications.wedding_rate_reminder")} />}
      <TinyText text={t("notifications.wedding_rate_reminder.description1")} />
      <br />
      <TinyText text={t("notifications.wedding_rate_reminder.description2")} />
      <SmallCheckBox
        label={t("global.enabled")!}
        onChange={() => setEnabled((prev) => !prev)}
        defaultValue={enabled}
      />
      <br />
      <div className="mt-3">
        <p className="text-sm font-extrabold">{t("notifications.delivery.label")}:</p>
        <NotificationDeliveryToggle
          variant="three_sms_disabled"
          className="mt-2"
          value={fmt}
          onChange={setEmailBodyFormat}
        />
      </div>
      <Notice
        pillText={t("global.notice")}
        tinyText={t("notifications.wedding_rate_reminder.email_message.keywords")}
      />
      <div className="mt-3 space-y-3">
        <InputShortText
          label={t("notifications.wedding_rate_reminder.email_title")!}
          validationFn={(value) => !!value && isValidText(value, 1, 50)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailTitle(e.target.value)}
          errorMessage={"errors.too_long_short"}
          defaultValue={emailTitle}
        />
        {fmt === "plain" ? (
          <TextArea
            label={t("notifications.wedding_rate_reminder.email_plain_label")!}
            onChange={(e) => setText(e.currentTarget.value)}
            defaultValue={text}
            errorMessage={"errors.too_long"}
            maxLength={10000}
          />
        ) : (
          <div className="mt-1">
            <EmailHtmlDualEditor
              label={t("notifications.wedding_rate_reminder.email_html")!}
              value={text}
              onChange={setText}
              maxLength={100000}
            />
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <SecondaryButton
          text={String(t("notifications.wedding_rate_reminder.use_default_template"))}
          fn={injectDefaultTemplate}
        />
        <SecondaryButton
          text={String(t("notifications.test_email"))}
          fn={sendTest}
          isDisabled={testLoading}
        />
      </div>
    </div>
  );
}
